import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Shield, Lock, Eye, Database, AlertTriangle } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
            <Shield className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-500">Last updated: December 2024</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Important Notice */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold text-amber-800 mb-2">IMPORTANT DISCLAIMER</h3>
                  <p className="text-amber-700 text-sm leading-relaxed">
                    Neon Alpha provides tools and information for <strong>ENTERTAINMENT AND INFORMATIONAL 
                    PURPOSES ONLY</strong>. We do not provide investment advice, and no information on this 
                    platform should be construed as financial, legal, or tax advice. Always conduct your own 
                    due diligence and consult qualified professionals before making investment decisions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye size={20} className="text-purple-600" />
                1. Introduction
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                Neon Alpha ("we," "us," or "our") respects your privacy and is committed to protecting your 
                personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you use our stock analysis platform and related services (the "Service").
              </p>
              <p>
                By using the Service, you consent to the data practices described in this Privacy Policy. 
                If you do not agree with our policies, please do not use the Service.
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database size={20} className="text-purple-600" />
                2. Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <h4 className="font-semibold text-gray-900">2.1 Information You Provide</h4>
              <ul>
                <li><strong>Account Information:</strong> Email address, name, and password when you create an account</li>
                <li><strong>Profile Information:</strong> Any additional information you choose to add to your profile</li>
                <li><strong>Journal Entries:</strong> Trade journal entries, notes, and analysis you create</li>
                <li><strong>Search History:</strong> Stock symbols and queries you search for</li>
                <li><strong>Payment Information:</strong> Billing details processed through our payment provider (Stripe)</li>
              </ul>

              <h4 className="font-semibold text-gray-900 mt-4">2.2 Information Collected Automatically</h4>
              <ul>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Service</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                <li><strong>Cookies:</strong> Session cookies and authentication tokens</li>
              </ul>

              <h4 className="font-semibold text-gray-900 mt-4">2.3 Information from Third Parties</h4>
              <p>
                We integrate with third-party financial data providers (such as Yahoo Finance) to provide 
                stock information. We do not receive personal information about you from these providers.
              </p>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card>
            <CardHeader>
              <CardTitle>3. How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve the Service</li>
                <li>Process your account registration and manage your subscription</li>
                <li>Store and display your trade journal entries</li>
                <li>Personalize your experience (e.g., showing recent searches)</li>
                <li>Process payments and prevent fraud</li>
                <li>Send you important updates about the Service</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Analyze usage patterns to improve our features</li>
                <li>Comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card>
            <CardHeader>
              <CardTitle>4. How We Share Your Information</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> With third-party vendors who assist us in operating the Service (e.g., Stripe for payment processing, hosting providers)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              </ul>
              <p>
                Aggregated, anonymized data that cannot identify you may be shared for analytics and research purposes.
              </p>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock size={20} className="text-purple-600" />
                5. Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                We implement appropriate technical and organizational measures to protect your personal data against 
                unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul>
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Secure password hashing</li>
                <li>Regular security assessments</li>
                <li>Access controls and authentication</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your data, we cannot guarantee absolute security.
              </p>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card>
            <CardHeader>
              <CardTitle>6. Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                We retain your personal data for as long as your account is active or as needed to provide you 
                with the Service. We may also retain and use your information as necessary to:
              </p>
              <ul>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements</li>
              </ul>
              <p>
                If you close your account, we will delete or anonymize your personal data within a reasonable 
                timeframe, unless retention is required by law.
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle>7. Your Rights</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>Depending on your location, you may have the following rights regarding your personal data:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Objection:</strong> Object to certain processing of your data</li>
                <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us through the appropriate channels. We will respond 
                to your request within the timeframe required by applicable law.
              </p>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card>
            <CardHeader>
              <CardTitle>8. Cookies and Tracking</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Keep you logged in to your account</li>
                <li>Remember your preferences</li>
                <li>Analyze how our Service is used</li>
              </ul>
              <p>
                You can control cookies through your browser settings. Disabling cookies may affect some 
                functionality of the Service.
              </p>
            </CardContent>
          </Card>

          {/* Third-Party Links */}
          <Card>
            <CardHeader>
              <CardTitle>9. Third-Party Links</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                The Service may contain links to third-party websites or services. We are not responsible 
                for the privacy practices of these third parties. We encourage you to review the privacy 
                policies of any third-party sites you visit.
              </p>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card>
            <CardHeader>
              <CardTitle>10. Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                The Service is not intended for individuals under the age of 18. We do not knowingly collect 
                personal information from children. If you believe we have collected information from a child, 
                please contact us immediately.
              </p>
            </CardContent>
          </Card>

          {/* International Transfers */}
          <Card>
            <CardHeader>
              <CardTitle>11. International Data Transfers</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                Your information may be transferred to and processed in countries other than your country of 
                residence. These countries may have different data protection laws. By using the Service, 
                you consent to such transfers.
              </p>
            </CardContent>
          </Card>

          {/* Changes */}
          <Card>
            <CardHeader>
              <CardTitle>12. Changes to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new policy on this page and updating the "Last updated" date. Your continued use 
                of the Service after such changes constitutes acceptance of the updated policy.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>13. Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-gray-600">
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us 
                through the appropriate channels provided on our website.
              </p>
            </CardContent>
          </Card>

          {/* Final Note */}
          <Card className="border-gray-300 bg-gray-100">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-700 italic">
                Remember: All information provided by Neon Alpha is for entertainment and informational purposes 
                only. We do not provide investment advice, and you should always conduct your own due diligence 
                before making any investment decisions.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
